import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleContainer, EmptyState, Icon, Modal, Button, SelectButton } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import Style from './restrictRate.module.css';
import RatesManager, { generateClassNameRateName, SortRatesButton } from '../../rates/components/rateManager';
import { InheritedProperty } from '../../rates/ratesList';
import StyleRM from '../../rates/components/rateManager.module.css';
import { useSegment } from '../../../components/tracking/trackingProvider';
import RateModel from '../../../models/rateModel';
import LoadingCard from '../../../components/loadingCard';

export default function RestrictRate({ event, organization, onDiscounts, discountForm }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(false);
    const { ratesToSelect, type } = discountForm;
    const [selectedRatesAdd, setSelectedRatesAdd] = React.useState([]);
    const [selectedRatesDelete, setSelectedRatesDelete] = React.useState([]);
    const { track } = useSegment();

    const [allRates, loadingRates] = RateModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            query: {
                limit: 1000
            }
        },
        [organization, event]
    );

    const notLinkedRates = React.useMemo(() => {
        if (!allRates) return [];

        return allRates.filter(({ id }) => !ratesToSelect.includes(id)).map(({ object }) => object.id);
    }, [allRates, ratesToSelect]);

    const onUpdateRates = React.useCallback(
        newValue => {
            const updatedRates = newValue.map(rate => rate.object?.id || rate);
            onDiscounts('ratesToSelect', updatedRates);
            setSelectedRatesAdd([]);
            setSelectedRatesDelete([]);
            setIsOpen(false);
        },
        [onDiscounts]
    );

    const onDeleteRates = React.useCallback(
        toDelete => {
            const deleteIds = toDelete.map(rate => rate.object?.id || rate);
            const updatedRates = ratesToSelect.filter(rateId => !deleteIds.includes(rateId));
            onUpdateRates(updatedRates);
        },
        [ratesToSelect, onUpdateRates]
    );

    const onToggle = React.useCallback(
        isChecked => {
            if (!isChecked) {
                onDiscounts('ratesToSelect', []);
            }
        },
        [onDiscounts]
    );

    const toggleModal = React.useCallback(() => {
        setIsOpen(prevIsOpen => !prevIsOpen);
    }, []);

    //custom rates cell with cross icon (for delete)
    let getTableCells = React.useCallback(
        (hasDelete = false) => {
            return [
                {
                    header: t('ticket.rates.rateName'),
                    renderRate: (rate, { groups }) => {
                        // do not display icon for actual waiting list
                        return (
                            <div className={Style['cell-group']}>
                                <InheritedProperty
                                    displayIcon={false}
                                    property="name"
                                    object={rate}
                                    handleProperty={name => (
                                        <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                            {name}
                                        </div>
                                    )}
                                    template="rate_template"
                                />
                                {hasDelete && <Icon name="cross" onClick={() => onDeleteRates([rate.id])} className={Style['delete-icon']} />}
                            </div>
                        );
                    },
                    renderGroup: (group, onToggle) => {
                        return (
                            <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                                {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                                <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                                <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                            </div>
                        );
                    }
                }
            ];
        },
        [event, organization, onDeleteRates]
    );
    return (
        <ToggleContainer
            className={Style['restrict-toggle']}
            subtitle={t('ticket.discountForm.restrict.subtitle')}
            title={t('ticket.discountForm.restrict.title')}
            disabled={type === 'grid'}
            toggle={Boolean(ratesToSelect.length) && type !== 'grid'}
            onToggle={toggle => {
                track('ACTION > RATES LIMITED USES', { 'Page Title': 'CREATE DISCOUNT', Application: 'WeezTicket nBO', Activated: toggle ? 'True' : 'False' });
                onToggle();
            }}
        >
            <LoadingCard loading={loadingRates}>
                <Modal
                    size="large"
                    open={isOpen}
                    onClose={toggleModal}
                    title={t('ticket.discountForm.restrict.modal.title')}
                    subtitle={t('ticket.discountForm.restrict.modal.subtitle')}
                    cancelButton={<Button onClick={toggleModal} label={t('ticket.common.action.cancel')} />}
                    submitButton={<Button primary onClick={() => onUpdateRates(ratesToSelect.concat(selectedRatesAdd))} label={t('ticket.common.action.add')} />}
                >
                    <div className={Style['rates-manager-container']}>
                        <RatesManager
                            event={event}
                            organization={organization}
                            initialQuery={{
                                organizationId: organization.id,
                                eventId: event.id
                            }}
                            tableCells={getTableCells()}
                            mode={'other'}
                            onRatesSelected={setSelectedRatesAdd}
                            hideEmptyGroup
                            excludeRatesIds={ratesToSelect}
                            selectable={true}
                            activeSyncRates={false}
                            activeCacheRates
                            activeCacheGroups
                        />
                    </div>
                </Modal>
                {ratesToSelect.length > 0 ? (
                    <RatesManager
                        event={event}
                        organization={organization}
                        initialQuery={{
                            organizationId: organization.id,
                            eventId: event.id
                        }}
                        excludeRatesIds={notLinkedRates}
                        tableCells={getTableCells(true)}
                        mode={'other'}
                        hideEmptyGroup
                        selectable
                        activeSyncRates={false}
                        activeCacheRates
                        activeCacheGroups
                        selected={selectedRatesDelete}
                        onRatesSelected={setSelectedRatesDelete}
                    >
                        <div className={Style['container-global-actions']}>
                            <SelectButton
                                tooltipTitle={t('ticket.rates.noActionTooltip')}
                                title={selectedRatesDelete.length > 0 ? t('ticket.rates.rateAction', { count: selectedRatesDelete.length }) : t('ticket.common.action.bulk')}
                                objects={selectedRatesDelete}
                            >
                                <Button onClick={() => onDeleteRates(selectedRatesDelete)}>{t('ticket.cross-selling.listVariant.deleteAll')}</Button>
                            </SelectButton>
                            <div className={'container-secondary-actions'}>
                                <SortRatesButton disabled={false} onClick={() => setIsOpen(true)} label={t('ticket.creationEvent.recap.addRates')} icon="plus" />
                            </div>
                        </div>
                    </RatesManager>
                ) : (
                    <EmptyState
                        icon={<Icon name="list" />}
                        title={t('ticket.discountForm.restrict.emptyState.title')}
                        subtitle={t('ticket.discountForm.restrict.emptyState.subtitle')}
                        labelButton={t('ticket.discountForm.restrict.emptyState.button')}
                        onClick={toggleModal}
                    />
                )}
            </LoadingCard>
        </ToggleContainer>
    );
}

RestrictRate.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
